import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  Pagination,
  TextField,
  Button,
} from "@mui/material";
import API_BASE_URL from "../config";

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const jobsPerPage = 6;

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/jobs/alljobs`);
        setJobs(response.data.jobs);
        setTotalJobs(response.data.totalJobs);
        setFilteredJobs(response.data.jobs);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    // Filter jobs based on search query
    const filtered = jobs.filter((job) =>
      job.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredJobs(filtered);
    setPage(1); // Reset to first page when search query changes
  }, [searchQuery, jobs]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await axios.put(
        // `http://localhost:5000/api/jobs/${id}`,
        `${API_BASE_URL}/jobs/${id}`,
        { status: newStatus },
        { headers: { "Content-Type": "application/json" } }
      );
      setJobs((prev) =>
        prev.map((job) =>
          job.id === id ? { ...job, status: response.data.job.status } : job
        )
      );
    } catch (error) {
      console.error("Error updating job status:", error);
      alert("Failed to update status.");
    }
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job?")) return;

    try {
      // await axios.delete(`http://localhost:5000/api/jobs/${id}`);
      await axios.delete(`h${API_BASE_URL}/jobs/${id}`);
      setJobs((prev) => prev.filter((job) => job.id !== id));
      setTotalJobs((prev) => prev - 1);
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Failed to delete job.");
    }
  };

  // Pagination logic: Slice filteredJobs based on current page
  const paginatedJobs = filteredJobs.slice(
    (page - 1) * jobsPerPage,
    page * jobsPerPage
  );

  if (loading) return <Typography>Loading jobs...</Typography>;

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 4 }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        textAlign="center"
        mb={3}
        color="#42326e"
      >
        Jobs
      </Typography>

      <Box display="flex" justifyContent="right" mb={4}>
        <TextField
          label="Search by Job Title"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          sx={{ maxWidth: 500 }}
        />
      </Box>

      {filteredJobs.length === 0 ? (
        <Typography textAlign="center">No jobs found.</Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {paginatedJobs.map((job) => {
              return (
                <Grid item xs={12} sm={6} md={4} key={job.id}>
                  <Card
                    sx={{
                      height: "100%",
                      boxShadow: "1px 1px 2px 2px rgba(94, 238, 207, 0.6)",
                    }}
                  >
                    <CardHeader
                      title={job.title}
                      titleTypographyProps={{ fontWeight: "bold" }}
                      subheader={`Posted on ${new Date(
                        job.created_at
                      ).toLocaleDateString()}`}
                    />
                    <CardContent>
                      <Box
                        mt={1}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box>
                          <Typography>
                            <strong>Company:</strong> {job.company || "N/A"}
                          </Typography>
                          <Typography>
                            <strong>Location:</strong> {job.location || "N/A"}
                          </Typography>
                          <Typography>
                            <strong>Salary:</strong> {job.salary || "N/A"}
                          </Typography>
                          <Typography>
                            <strong>Status:</strong> {job.status}
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-end"
                          ml={2}
                        >
                          <TextField
                            select
                            label="Status"
                            value={job.status}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleStatusChange(job.id, value);
                            }}
                            SelectProps={{ native: true }}
                            size="small"
                            sx={{ mb: 1, width: 120 }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </TextField>

                          <Button
                            onClick={() => handleDeleteJob(job.id)}
                            sx={{
                              backgroundColor: "red",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: 2,
                              cursor: "pointer",
                              width: 120,
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {filteredJobs.length > jobsPerPage && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(filteredJobs.length / jobsPerPage)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminJobs;
